// Thao tác cần cả POS lẫn thẻ liệu trình: bán thẻ, thu tiền đợt, hoàn tiền, đồng bộ; quyền thao tác
import { useEffect } from 'react';
import useAuthStore from 'store/authStore';
import type { CartLine, CatalogItem, Customer, PaymentLineInput, PaymentMethod, PosRole } from '../POS/data';
import { calcOrder } from '../POS/pricing';
import usePosStore from '../POS/store';
import { currentUserName } from '../POS/utils';
import type { CardRefund, Installment, TreatmentCard, TreatmentPackage } from './data';
import useTreatmentStore from './store';
import { effectivePrice, saleListPrice } from './logic';

type TAction = 'view' | 'create' | 'edit' | 'delete' | 'approve' | 'export';
const ROLE_ACTIONS: Record<PosRole, TAction[]> = {
  cashier: ['view', 'create'],
  manager: ['view', 'create', 'edit', 'delete', 'approve', 'export'],
  admin: ['view', 'create', 'edit', 'delete', 'approve', 'export'],
};

/** Quyền thẻ liệu trình: super admin toàn quyền; dev có thể giả lập vai trò bằng công tắc dùng chung với POS */
export const useTreatmentAuth = () => {
  const { isSuperAdmin, permissions } = useAuthStore();
  const demoRole = usePosStore(s => s.demoRole);
  const can = (action: TAction) => (demoRole ? ROLE_ACTIONS[demoRole].includes(action) : isSuperAdmin || permissions.includes(`treatment.${action}`));
  return { can, userName: currentUserName(), isDemo: !!demoRole };
};

const posSnapshot = () => {
  const p = usePosStore.getState();
  return { orders: p.orders, transactions: p.transactions, refunds: p.refunds, customers: p.customers };
};

/** Phát hành thẻ cho gói vừa bán ở POS và ghi tiền thu / hoàn mới vào thẻ */
export const syncTreatmentWithPos = () => useTreatmentStore.getState().reconcileWithPos(posSnapshot());

/** Gọi ở mọi màn thẻ liệu trình: đồng bộ khi dữ liệu POS đổi */
export function useTreatmentSync() {
  const orders = usePosStore(s => s.orders);
  const transactions = usePosStore(s => s.transactions);
  const refunds = usePosStore(s => s.refunds);
  const packages = useTreatmentStore(s => s.packages);
  useEffect(() => { syncTreatmentWithPos(); }, [orders, transactions, refunds, packages]);
}

// ---------------------------------------------------------------------------

export interface SellInput {
  customer: Customer;
  packageId: string;
  branchId: string;
  discountType: '%' | 'đ';
  discountValue: number;
  consultantId: string;
  plan: Installment[];
  first: { method: PaymentMethod; amount: number; txnRef: string };
  note: string;
}

/**
 * Giá bán thẻ: giá áp dụng của gói (chi nhánh / khuyến mãi) + phụ thu trả góp, rồi qua bộ máy giá POS
 * (khuyến mãi tự động, chiết khấu, làm tròn)
 */
export function quoteSale(input: Pick<SellInput, 'packageId' | 'branchId' | 'discountType' | 'discountValue' | 'consultantId'> & { installments: number }) {
  const pkg = useTreatmentStore.getState().packages.find(p => p.id === input.packageId);
  if (!pkg) return null;
  const pos = usePosStore.getState();
  const sale = saleListPrice(pkg, input.branchId, input.installments);
  const line: CartLine = {
    key: `tlt-${pkg.id}-${Date.now()}`, itemId: pkg.id, code: pkg.code, name: pkg.name, type: 'package', listPrice: sale.listPrice, qty: 1,
    discountType: input.discountType, discountValue: input.discountValue, staffId: input.consultantId, note: '',
  };
  const pricing = calcOrder([line], {
    branchId: input.branchId, promotions: pos.promotions, orderDiscount: { type: '%', value: 0 }, voucher: null, roundingUnit: pos.config.roundingUnit,
  });
  return { pkg, line, pricing, sale };
}

/** Bán thẻ: tạo đơn POS (tiền vào sổ quỹ POS) rồi phát hành thẻ với lịch thanh toán đã chọn */
export function sellCard(input: SellInput): { card: TreatmentCard | null; orderId: string } {
  const q = quoteSale({ ...input, installments: input.plan.length });
  if (!q) throw new Error('Không tìm thấy gói');
  const by = currentUserName();
  const payments: PaymentLineInput[] = input.first.amount > 0
    ? [{ method: input.first.method, amount: input.first.amount, tendered: input.first.amount, txnRef: input.first.txnRef }]
    : [];
  const { order } = usePosStore.getState().createOrder({
    branchId: input.branchId, cashier: by, customer: input.customer, pricing: q.pricing, orderDiscountInput: { type: '%', value: 0 },
    voucherCode: '', note: input.note || `Bán ${q.pkg.name} – ${input.plan.length} đợt`, payments,
  });
  const card = useTreatmentStore.getState().issueCard({
    order, lineKey: order.lines[0].key, customer: input.customer, plan: input.plan, consultant: '', by,
  });
  syncTreatmentWithPos();
  return { card: card && useTreatmentStore.getState().cards.find(c => c.id === card.id)!, orderId: order.id };
}

/** Còn có thể thu qua POS cho thẻ này (đơn POS còn nợ) */
export const posDebtOf = (card: TreatmentCard) => (card.orderId ? usePosStore.getState().orders.find(o => o.id === card.orderId)?.debt ?? 0 : 0);

/** Thu tiền đợt: thẻ bán qua POS thì thu nợ trên đơn POS (để khớp quỹ), thẻ chuyển đổi thì ghi thẳng vào thẻ */
export function payCard(card: TreatmentCard, p: { amount: number; method: PaymentMethod; txnRef: string; tendered: number; branchId: string }): { change: number; paymentId: string | null } {
  const by = currentUserName();
  const before = new Set(card.payments.map(x => x.id));
  // Mã thanh toán mới sinh ra trên thẻ — dùng để in phiếu thu ngay sau khi thu
  const newPaymentId = () => {
    const fresh = useTreatmentStore.getState().cards.find(c => c.id === card.id);
    const added = (fresh?.payments || []).filter(x => !before.has(x.id));
    return added.length ? added[added.length - 1].id : null;
  };
  if (card.source === 'pos' && card.orderId) {
    const res = usePosStore.getState().collectDebt(card.orderId, [{ method: p.method, amount: p.amount, tendered: p.tendered, txnRef: p.txnRef }], by);
    syncTreatmentWithPos();
    return { change: res.change, paymentId: newPaymentId() };
  }
  useTreatmentStore.getState().recordPayment(card.id, { amount: p.amount, method: p.method, txnRef: p.txnRef, by, branchId: p.branchId });
  return { change: p.method === 'cash' ? Math.max(0, p.tendered - p.amount) : 0, paymentId: newPaymentId() };
}

/**
 * Hoàn tiền thẻ. Thẻ bán qua POS: tạo phiếu hoàn trên đơn POS (duyệt / chi tiền ở màn Hoàn tiền POS),
 * thẻ chuyển đổi: dưới ngưỡng thì chi ngay, từ ngưỡng trở lên chờ quản lý duyệt.
 */
export function refundCard(
  card: TreatmentCard,
  r: Pick<CardRefund, 'mode' | 'sessions' | 'valueReturned' | 'amount' | 'fee' | 'method' | 'txnRef' | 'reason'>,
  canApprove: boolean,
): CardRefund {
  const by = currentUserName();
  const t = useTreatmentStore.getState();
  if (card.source === 'pos' && card.orderId) {
    const order = usePosStore.getState().orders.find(o => o.id === card.orderId);
    const line = order?.lines.find(l => l.key === card.lineKey);
    if (!order || !line) throw new Error('Không tìm thấy đơn POS của thẻ');
    const posRefund = usePosStore.getState().createRefund({
      orderId: order.id, lines: [{ lineKey: line.key, name: line.name, qty: r.mode === 'full' ? line.qty - line.refundedQty : 0, amount: r.amount }],
      method: r.method, txnRef: r.txnRef, reason: r.reason, by, canApprove,
    });
    const refund = t.addRefund(card.id, { ...r, by, status: posRefund.status === 'paid' ? 'paid' : 'pending', posRefundId: posRefund.id });
    syncTreatmentWithPos();
    return refund;
  }
  const needApproval = !canApprove && r.amount >= t.config.refundApprovalThreshold;
  return t.addRefund(card.id, { ...r, by, status: needApproval ? 'pending' : 'paid', ...(needApproval ? {} : { approvedBy: by, approvedAt: new Date().toISOString() }) });
}

/** Danh mục POS: thay nhóm "Gói" tĩnh bằng các gói liệu trình đang bán, giá theo chi nhánh / khuyến mãi */
export function withTreatmentPackages(base: CatalogItem[], packages: TreatmentPackage[], branchId: string): CatalogItem[] {
  const items: CatalogItem[] = packages.filter(p => p.active).map(p => {
    const eff = effectivePrice(p, branchId);
    return {
      id: p.id, code: p.code, name: p.name, price: eff.price, type: 'package', group: p.category, stock: null,
      sessions: p.kind === 'session' ? p.items.reduce((s, i) => s + i.sessions, 0) : undefined,
      badge: eff.source === 'promo' ? 'KM' : p.kind === 'value' ? 'Thẻ giá trị' : p.maxInstallments > 1 ? 'Trả góp' : undefined,
    };
  });
  return [...base.filter(c => c.type !== 'package'), ...items];
}
